import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';

import { useCreateProjectType } from '@/hooks/api/useProjects';
import { useDialogManager } from '@/hooks/useDialogManager';
import { ProjectTypeAssetForm } from './tabs/AssetsTab';

import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SimpleForm } from '@/components/SimpleForm';
import { FormInput } from '@/components/shared/FormInput';
import { FormTextarea } from '@/components/shared/FormTextArea';

const createProjectSchema = z.object({
  name: z.string().min(1, 'Project type name is required'),
  description: z.string().optional(),
  assets: z.any(), // Adjust based on your AssetsTab data structure (likely array of IDs)
});

type CreateProjectFormValues = z.infer<typeof createProjectSchema>;

export const CreateProjectTypeForm = () => {
  const { closeDialog } = useDialogManager();
  const { createProjectType, isCreating } = useCreateProjectType();

  const form = useForm<CreateProjectFormValues>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: {
      name: '',
      description: '',
      assets: [], // Default to empty array
    },
  });

  const onSubmit = (formData: CreateProjectFormValues) => {
    createProjectType(formData, {
      onSuccess: () => {
        toast.success('New project type created!');
        closeDialog();
      },
      onError: () => toast.error('Could not create project type.'),
    });
  };

  return (
    <SimpleForm
      methods={form}
      onSubmit={onSubmit}
      onCancel={closeDialog}
      submitText="Create Project Type"
      isSubmitting={isCreating}
    >
      <FormInput
        type="text"
        name="name"
        label="Project Type Name"
        placeholder="Name..."
      />

      <FormTextarea
        name="description"
        label="Description"
        rows={5}
        placeholder="Enter description..."
      />

      <div className="pt-2">
        <h4 className="text-sm font-medium mb-3">Default Assets</h4>
        <ProjectTypeAssetForm control={form.control} name="assets" />
      </div>
    </SimpleForm>
  );
};
